import { lazy } from 'react';

import LegacyModuleFrame from '@/components/layout/LegacyModuleFrame';
import MainLayout from '@/components/layout/MainLayout';
import { AuthGuard } from '@/components/routing/AuthGuard';
import { ErrorFallback } from '@/components/ui/ErrorFallback';
import { NotFoundScreen } from '@/components/ui/NotFoundScreen';
import { MODES, type ModeString } from '@/consts/modes';
import { ROUTES } from '@/consts/routes';
import { env } from '@/env/env';
import { LoginScreen } from '@/features/auth/login';
import { PasswordResetScreen } from '@/features/auth/password-reset';
import { ISM } from '@/modules/ISM/views/ISM';
import { AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { createBrowserRouter } from 'react-router';

const DefineComponentLibraryLayout = lazy(
  () => import('@/modules/PMS/features/define-component-library/components/DefineComponentLibraryLayout')
);
const ComponentsView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/ComponentsView'));
const SparesView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/SparesView'));
const JobsView = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/JobsView'));
const VesselComponentLibraryView = lazy(
  () => import('@/modules/PMS/views/VesselComponentLibrary/VesselComponentLibraryView')
);
const MainLibrariesView = lazy(() => import('@/modules/PMS/views/MainLibraries/MainLibrariesView'));
const AbstractComponentEditPage = lazy(
  () => import('@/modules/PMS/views/DefineComponentLibrary/AbstractComponentEditPage')
);
const SpareEditPage = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/SpareEditPage'));
const JobEditPage = lazy(() => import('@/modules/PMS/views/DefineComponentLibrary/JobEditPage'));
const ApiDocsPage = lazy(() => import('@/features/api-docs/views/ApiDocsPage'));

const baseName = env.VITE_MOCK_MODE === 'true' ? '/' : env.VITE_BASE_URL;
const baseHostName = window.location.origin;
const shiplexBaseUrl = (env.VITE_SHIPLEX_BASE_URL ?? baseHostName) + env.VITE_SHIPLEX_LEGACY_APP_ROUTE;
const buildSrc = (mode: ModeString) => `${shiplexBaseUrl}?mode=${mode}`;

export const router = createBrowserRouter(
  [
    { path: ROUTES.auth.login, element: <LoginScreen /> },
    { path: ROUTES.user.passwordReset, element: <PasswordResetScreen /> },
    { path: ROUTES.auth.sso, element: <AuthenticateWithRedirectCallback /> },
    {
      path: '/',
      element: (
        <AuthGuard>
          <MainLayout />
        </AuthGuard>
      ),
      errorElement: <ErrorFallback />,
      children: [
        { path: ROUTES.ism.drills.replace('/', ''), element: <ISM /> },
        // API Documentation routes
        {
          path: 'docs/api',
          element: <ApiDocsPage />,
          loader: () => ({ crumb: 'API Documentation' })
        },
        {
          path: 'docs/api/:module',
          element: <ApiDocsPage />,
          loader: () => ({ crumb: 'API Documentation' })
        },
        {
          path: 'docs/api/:module/:entity',
          element: <ApiDocsPage />,
          loader: () => ({ crumb: 'API Documentation' })
        },
        {
          path: ROUTES.pms.defineComponentLibrary.replace('/', ''),
          element: <DefineComponentLibraryLayout />,
          loader: () => ({ crumb: 'Define Component Library' }),
          children: [
            { index: true, element: <ComponentsView />, loader: () => ({ crumb: 'Components' }) },
            { path: 'components', element: <ComponentsView />, loader: () => ({ crumb: 'Components' }) },
            { path: 'spares', element: <SparesView />, loader: () => ({ crumb: 'Spares' }) },
            { path: 'jobs', element: <JobsView />, loader: () => ({ crumb: 'Jobs' }) }
          ]
        },
        // Component edit pages - rendered outside DefineComponentLibraryLayout to hide top tabs
        {
          path: 'pms/define-component-library/components/new',
          element: <AbstractComponentEditPage />,
          loader: () => ({ crumb: 'New Component' })
        },
        {
          path: 'pms/define-component-library/components/:id/edit',
          element: <AbstractComponentEditPage />,
          loader: () => ({ crumb: 'Edit Component' })
        },
        // Spare edit pages - rendered outside DefineComponentLibraryLayout to hide top tabs
        {
          path: 'pms/define-component-library/spares/new',
          element: <SpareEditPage />,
          loader: () => ({ crumb: 'New Spare' })
        },
        {
          path: 'pms/define-component-library/spares/:id/edit',
          element: <SpareEditPage />,
          loader: () => ({ crumb: 'Edit Spare' })
        },
        // Job edit pages - rendered outside DefineComponentLibraryLayout to hide top tabs
        {
          path: 'pms/define-component-library/jobs/new',
          element: <JobEditPage />,
          loader: () => ({ crumb: 'New Job' })
        },
        {
          path: 'pms/define-component-library/jobs/:id/edit',
          element: <JobEditPage />,
          loader: () => ({ crumb: 'Edit Job' })
        },
        {
          path: ROUTES.pms.vesselComponentLibrary.replace('/', ''),
          element: <VesselComponentLibraryView />,
          loader: () => ({ crumb: 'Vessel Components Library' })
        },
        {
          path: ROUTES.pms.mainLibraries.replace('/', ''),
          element: <MainLibrariesView />,
          loader: () => ({ crumb: 'Main Libraries' })
        },

        { path: ROUTES.legacy.ism.drills, element: <LegacyModuleFrame src={buildSrc(MODES.ism.drills)} /> },
        { path: ROUTES.legacy.ism.drillTypes, element: <LegacyModuleFrame src={buildSrc(MODES.ism.drillTypes)} /> },

        { path: ROUTES.legacy.voy.comp, element: <LegacyModuleFrame src={buildSrc(MODES.voy.comp)} /> },
        { path: ROUTES.legacy.voy.calcDet, element: <LegacyModuleFrame src={buildSrc(MODES.voy.calcDet)} /> },
        { path: ROUTES.legacy.voy.voyages, element: <LegacyModuleFrame src={buildSrc(MODES.voy.voyages)} /> },
        { path: ROUTES.legacy.voy.tracker, element: <LegacyModuleFrame src={buildSrc(MODES.voy.tracker)} /> },
        { path: ROUTES.legacy.voy.tceOverview, element: <LegacyModuleFrame src={buildSrc(MODES.voy.tceOverview)} /> },
        {
          path: ROUTES.legacy.voy.unsyncedClaims,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.unsyncedClaims)} />
        },
        { path: ROUTES.legacy.voy.accTracker, element: <LegacyModuleFrame src={buildSrc(MODES.voy.accTracker)} /> },
        { path: ROUTES.legacy.voy.works, element: <LegacyModuleFrame src={buildSrc(MODES.voy.works)} /> },
        { path: ROUTES.legacy.voy.opdash, element: <LegacyModuleFrame src={buildSrc(MODES.voy.opdash)} /> },
        {
          path: ROUTES.legacy.voy.voyageSearch,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.voyageSearch)} />
        },
        {
          path: ROUTES.legacy.voy.bunkeringsuppliesreport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.bunkeringsuppliesreport)} />
        },
        {
          path: ROUTES.legacy.voy.itineraryofvesselreport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.itineraryofvesselreport)} />
        },
        {
          path: ROUTES.legacy.voy.cargohistoryandanalysisreport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.cargohistoryandanalysisreport)} />
        },
        {
          path: ROUTES.legacy.voy.cargoreport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.cargoreport)} />
        },
        {
          path: ROUTES.legacy.voy.perfreport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.perfreport)} />
        },
        { path: ROUTES.legacy.voy.pcreport, element: <LegacyModuleFrame src={buildSrc(MODES.voy.pcreport)} /> },
        {
          path: ROUTES.legacy.voy.reportNoonDetails,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.reportNoonDetails)} />
        },
        {
          path: ROUTES.legacy.voy.reportVoyageDailyPerformance,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.reportVoyageDailyPerformance)} />
        },
        {
          path: ROUTES.legacy.voy.reportLatestNoonReports,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.reportLatestNoonReports)} />
        },
        {
          path: ROUTES.legacy.voy.dailyNoonPerformance,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.dailyNoonPerformance)} />
        },
        {
          path: ROUTES.legacy.voy.dailyNoonPerformancePerday,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.dailyNoonPerformancePerday)} />
        },
        {
          path: ROUTES.legacy.voy.stoppageListReport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.stoppageListReport)} />
        },
        {
          path: ROUTES.legacy.voy.consumptionsPerGradeReport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.consumptionsPerGradeReport)} />
        },
        {
          path: ROUTES.legacy.voy.consumptionsPerGradeReportDetailed,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.consumptionsPerGradeReportDetailed)} />
        },
        {
          path: ROUTES.legacy.voy.weeklyLubricantsReport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.weeklyLubricantsReport)} />
        },
        {
          path: ROUTES.legacy.voy.daysAnalysisReport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.daysAnalysisReport)} />
        },
        {
          path: ROUTES.legacy.voy.cargoStatisticsReport,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.cargoStatisticsReport)} />
        },
        { path: ROUTES.legacy.voy.consCurves, element: <LegacyModuleFrame src={buildSrc(MODES.voy.consCurves)} /> },
        {
          path: ROUTES.legacy.voy.ciiReportVoyages,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.ciiReportVoyages)} />
        },
        { path: ROUTES.legacy.voy.settings, element: <LegacyModuleFrame src={buildSrc(MODES.voy.settings)} /> },
        {
          path: ROUTES.legacy.voy.portrestrictions,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.portrestrictions)} />
        },
        { path: ROUTES.legacy.voy.cptypes, element: <LegacyModuleFrame src={buildSrc(MODES.voy.cptypes)} /> },
        {
          path: ROUTES.legacy.voy.indexValues,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.indexValues)} />
        },
        {
          path: ROUTES.legacy.voy.porteventtypes,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.porteventtypes)} />
        },
        { path: ROUTES.legacy.voy.bjobs, element: <LegacyModuleFrame src={buildSrc(MODES.voy.bjobs)} /> },
        { path: ROUTES.legacy.voy.tasklists, element: <LegacyModuleFrame src={buildSrc(MODES.voy.tasklists)} /> },
        {
          path: ROUTES.legacy.voy.tasklistitems,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.tasklistitems)} />
        },
        {
          path: ROUTES.legacy.voy.tasklistitemcategories,
          element: <LegacyModuleFrame src={buildSrc(MODES.voy.tasklistitemcategories)} />
        },

        { path: ROUTES.legacy.bun.bunkering, element: <LegacyModuleFrame src={buildSrc(MODES.bun.bunkering)} /> },
        { path: ROUTES.legacy.bun.grades, element: <LegacyModuleFrame src={buildSrc(MODES.bun.grades)} /> },
        { path: ROUTES.legacy.bun.offers, element: <LegacyModuleFrame src={buildSrc(MODES.bun.offers)} /> },
        {
          path: ROUTES.legacy.bun.coqcategories,
          element: <LegacyModuleFrame src={buildSrc(MODES.bun.coqcategories)} />
        },
        {
          path: ROUTES.legacy.bun.bunkeringportprices,
          element: <LegacyModuleFrame src={buildSrc(MODES.bun.bunkeringportprices)} />
        },
        {
          path: ROUTES.legacy.bun.coqisostandards,
          element: <LegacyModuleFrame src={buildSrc(MODES.bun.coqisostandards)} />
        },
        {
          path: ROUTES.legacy.bun.proposedportcalls,
          element: <LegacyModuleFrame src={buildSrc(MODES.bun.proposedportcalls)} />
        },

        { path: ROUTES.legacy.fre.freight, element: <LegacyModuleFrame src={buildSrc(MODES.fre.freight)} /> },
        { path: ROUTES.legacy.fre.overview, element: <LegacyModuleFrame src={buildSrc(MODES.fre.overview)} /> },
        { path: ROUTES.legacy.fre.kpiDashboard, element: <LegacyModuleFrame src={buildSrc(MODES.fre.kpiDashboard)} /> },
        { path: ROUTES.legacy.fre.monitoring, element: <LegacyModuleFrame src={buildSrc(MODES.fre.monitoring)} /> },

        {
          path: ROUTES.legacy.pda.portexpenses,
          element: <LegacyModuleFrame src={buildSrc(MODES.pda.portexpenses)} />
        },
        {
          path: ROUTES.legacy.pda.accountingcode,
          element: <LegacyModuleFrame src={buildSrc(MODES.pda.accountingcode)} />
        },
        {
          path: ROUTES.legacy.pda.expensegroups,
          element: <LegacyModuleFrame src={buildSrc(MODES.pda.expensegroups)} />
        },
        {
          path: ROUTES.legacy.pda.expensetypes,
          element: <LegacyModuleFrame src={buildSrc(MODES.pda.expensetypes)} />
        },
        {
          path: ROUTES.legacy.pda.integrationnotifications,
          element: <LegacyModuleFrame src={buildSrc(MODES.pda.integrationnotifications)} />
        },
        {
          path: ROUTES.legacy.pda.voyagesintegration,
          element: <LegacyModuleFrame src={buildSrc(MODES.pda.voyagesintegration)} />
        },

        {
          path: ROUTES.legacy.master.vessels,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.vessels)} />
        },
        { path: ROUTES.legacy.master.tanks, element: <LegacyModuleFrame src={buildSrc(MODES.master.tanks)} /> },
        {
          path: ROUTES.legacy.master.vesselspecgroups,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.vesselspecgroups)} />
        },
        {
          path: ROUTES.legacy.master.vesselclasses,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.vesselclasses)} />
        },
        {
          path: ROUTES.legacy.master.licenseranks,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.licenseranks)} />
        },
        {
          path: ROUTES.legacy.master.dutyranks,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.dutyranks)} />
        },
        {
          path: ROUTES.legacy.master.payrollranks,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.payrollranks)} />
        },
        {
          path: ROUTES.legacy.master.vesseltypes,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.vesseltypes)} />
        },
        {
          path: ROUTES.legacy.master.companies,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.companies)} />
        },
        {
          path: ROUTES.legacy.master.fueltypes,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.fueltypes)} />
        },
        { path: ROUTES.legacy.master.ports, element: <LegacyModuleFrame src={buildSrc(MODES.master.ports)} /> },
        {
          path: ROUTES.legacy.master.cargotypes,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.cargotypes)} />
        },
        {
          path: ROUTES.legacy.master.cargogrades,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.cargogrades)} />
        },
        { path: ROUTES.legacy.master.flags, element: <LegacyModuleFrame src={buildSrc(MODES.master.flags)} /> },
        {
          path: ROUTES.legacy.master.persons,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.persons)} />
        },
        { path: ROUTES.legacy.master.ratings, element: <LegacyModuleFrame src={buildSrc(MODES.master.ratings)} /> },
        {
          path: ROUTES.legacy.master.companyRatings,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.companyRatings)} />
        },
        {
          path: ROUTES.legacy.master.customUsers,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.customUsers)} />
        },
        {
          path: ROUTES.legacy.master.departments,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.departments)} />
        },
        { path: ROUTES.legacy.master.deppos, element: <LegacyModuleFrame src={buildSrc(MODES.master.deppos)} /> },
        { path: ROUTES.legacy.master.roles, element: <LegacyModuleFrame src={buildSrc(MODES.master.roles)} /> },
        {
          path: ROUTES.legacy.master.usersettings,
          element: <LegacyModuleFrame src={buildSrc(MODES.master.usersettings)} />
        },

        {
          path: ROUTES.legacy.md.incomecategories,
          element: <LegacyModuleFrame src={buildSrc(MODES.md.incomecategories)} />
        },
        {
          path: ROUTES.legacy.md.incomereasons,
          element: <LegacyModuleFrame src={buildSrc(MODES.md.incomereasons)} />
        },
        {
          path: ROUTES.legacy.md.expenseCategories,
          element: <LegacyModuleFrame src={buildSrc(MODES.md.expenseCategories)} />
        },
        { path: ROUTES.legacy.md.areas, element: <LegacyModuleFrame src={buildSrc(MODES.md.areas)} /> },
        { path: ROUTES.legacy.md.countries, element: <LegacyModuleFrame src={buildSrc(MODES.md.countries)} /> },
        { path: ROUTES.legacy.md.cities, element: <LegacyModuleFrame src={buildSrc(MODES.md.cities)} /> },
        {
          path: ROUTES.legacy.md.nationalities,
          element: <LegacyModuleFrame src={buildSrc(MODES.md.nationalities)} />
        },
        {
          path: ROUTES.legacy.md.usercompanies,
          element: <LegacyModuleFrame src={buildSrc(MODES.md.usercompanies)} />
        },
        {
          path: ROUTES.legacy.md.userdepartments,
          element: <LegacyModuleFrame src={buildSrc(MODES.md.userdepartments)} />
        },
        {
          path: ROUTES.legacy.md.userpositions,
          element: <LegacyModuleFrame src={buildSrc(MODES.md.userpositions)} />
        },
        { path: ROUTES.legacy.md.userRoles, element: <LegacyModuleFrame src={buildSrc(MODES.md.userRoles)} /> },

        { path: ROUTES.legacy.admin.menu, element: <LegacyModuleFrame src={buildSrc(MODES.admin.menu)} /> },
        { path: ROUTES.legacy.admin.health, element: <LegacyModuleFrame src={buildSrc(MODES.admin.health)} /> },
        { path: ROUTES.legacy.admin.issues, element: <LegacyModuleFrame src={buildSrc(MODES.admin.issues)} /> },
        { path: ROUTES.legacy.admin.dashboard, element: <LegacyModuleFrame src={buildSrc(MODES.admin.dashboard)} /> },
        { path: ROUTES.legacy.admin.workItems, element: <LegacyModuleFrame src={buildSrc(MODES.admin.workItems)} /> },

        {
          path: ROUTES.legacy.notifications.dashboard,
          element: <LegacyModuleFrame src={buildSrc(MODES.notifications.dashboard)} />
        },

        {
          path: ROUTES.legacy.acc.freightPayments,
          element: <LegacyModuleFrame src={buildSrc(MODES.acc.freightPayments)} />
        },
        {
          path: ROUTES.legacy.acc.freightPaymentsList,
          element: <LegacyModuleFrame src={buildSrc(MODES.acc.freightPaymentsList)} />
        },
        { path: ROUTES.legacy.acc.bbase, element: <LegacyModuleFrame src={buildSrc(MODES.acc.bbase)} /> },

        {
          path: ROUTES.legacy.workflows.adminDashboard,
          element: <LegacyModuleFrame src={buildSrc(MODES.workflows.adminDashboard)} />
        },
        { path: ROUTES.legacy.workflows.tasks, element: <LegacyModuleFrame src={buildSrc(MODES.workflows.tasks)} /> },
        {
          path: ROUTES.legacy.workflows.invoices,
          element: <LegacyModuleFrame src={buildSrc(MODES.workflows.invoices)} />
        }
      ]
    },
    { path: '*', element: <NotFoundScreen /> }
  ],
  { basename: baseName }
);
